/**
 * Data Prefetching System
 * 
 * Provides QueryClient-integrated prefetch functions for sections and shared reference data.
 * Used for hover-prefetching and background preloading after login.
 */

import { QueryClient } from '@tanstack/react-query';
import { fromTable, supabase } from '@/integrations/supabase/db';

/**
 * Prefetch shared reference data used across multiple sections
 * Cached for 30 minutes due to low change frequency
 */
export const prefetchReferenceData = async (queryClient: QueryClient) => {
  // Sparten - used by Contracts, Dialogs
  queryClient.prefetchQuery({
    queryKey: ['sparten'],
    queryFn: async () => {
      const { data } = await supabase
        .from('sparten')
        .select('id, sparte')
        .order('sparte');
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
  
  // Gesellschaften - used by Contracts, Dialogs, Produktanbieter
  queryClient.prefetchQuery({
    queryKey: ['gesellschaften'],
    queryFn: async () => {
      const { data } = await supabase
        .from('gesellschaften')
        .select('id, name, logo_url, bereich')
        .order('name');
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
  
  // Lead-Bereiche - used by Leads
  queryClient.prefetchQuery({
    queryKey: ['lead-bereiche'],
    queryFn: async () => {
      const { data } = await supabase
        .from('lead_bereiche')
        .select('id, name')
        .order('name');
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Prefetch Leads section data
 */
export const prefetchLeadsData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['leads-rpc', userId, 1, 10, {}],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_leads_with_stats', {
        p_user_id: userId,
        p_filters: {},
        p_page: 1,
        p_page_size: 10
      });
      return data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Customers section data
 */
export const prefetchCustomersData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['customers-rpc', userId, 1, 50, {}],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_customers_with_stats', {
        p_user_id: userId,
        p_filters: {},
        p_page: 1,
        p_page_size: 50
      });
      return data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Contracts section data
 */
export const prefetchContractsData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['contracts-rpc', userId, 1, 50, '', '', 'all', 'all', 'all', 'all'],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_contracts_with_stats', {
        p_user_id: userId,
        p_filters: {},
        p_page: 1,
        p_page_size: 50
      });
      return data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Tasks (Meine Aufgaben) section data
 */
export const prefetchAufgabenData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['aufgaben-list', userId],
    queryFn: async () => {
      // FK-Embedding doesn't work with views - fetch aufgaben without FK join
      const { data } = await supabase
        .from('aufgaben')
        .select('*')
        .order('created_at', { ascending: false });
      return data || [];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Fremdverträge section data
 */
export const prefetchFremdvertraegeData = async (queryClient: QueryClient, userId: string, userEmail: string) => {
  queryClient.prefetchQuery({
    queryKey: ['fremdvertraege-list', userId, 1, 50, '', 'all', 'all'],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_fremdvertraege_list', {
        p_user_id: userId,
        p_user_email: userEmail,
        p_filters: { search: '', gesellschaftId: 'all', sparte: 'all', partner: 'all' },
        p_page: 1,
        p_page_size: 50
      });
      return data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Vertriebspartner section data
 */
export const prefetchVertriebspartnerData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['vertriebspartner-rpc', userId, 1, 50, {}],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_vertriebspartner_with_stats', {
        p_user_id: userId,
        p_filters: {},
        p_page: 1,
        p_page_size: 50
      });
      return data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Downloadcenter folder structure
 */
export const prefetchDownloadcenterData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['wissensdatenbank-documents'],
    queryFn: async () => {
      const { data } = await supabase
        .from('wissensdatenbank_dokumente')
        .select('*')
        .order('bezeichnung');
      return data || [];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Produktanbieter section data
 */
export const prefetchProduktanbieterData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['gesellschaften-with-documents'],
    queryFn: async () => {
      const { data } = await supabase
        .from('gesellschaften')
        .select('*')
        .order('name');
      return data || [];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Prefetch Dashboard data
 */
export const prefetchDashboardData = async (queryClient: QueryClient, userId: string) => {
  queryClient.prefetchQuery({
    queryKey: ['dashboard-data', userId, false],
    queryFn: async () => {
      const { data } = await supabase.rpc('get_dashboard_data', {
        p_user_id: userId,
        p_show_only_leadhelden: false
      });
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Prefetch upcoming events for widget
 */
export const prefetchEventsData = async (queryClient: QueryClient, userId: string) => {
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = new Date();
  const thirtyOneDaysFromNow = new Date();
  thirtyOneDaysFromNow.setDate(today.getDate() + 31);

  queryClient.prefetchQuery({
    queryKey: ['upcoming-events', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('upcoming_events')
        .select('*')
        .gte('event_date', formatLocalDate(today))
        .lte('event_date', formatLocalDate(thirtyOneDaysFromNow))
        .order('event_date', { ascending: true });
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (Phase 4: Sync with Dashboard)
  });
};

/**
 * Prefetch weekly calendar data
 */
export const prefetchCalendarData = async (queryClient: QueryClient, userId: string) => {
  const getStartOfWeek = (date: Date): string => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const getEndOfWeek = (date: Date): string => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? 0 : 7); // Sunday as last day
    d.setDate(diff);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  const startOfWeek = getStartOfWeek(new Date());
  const endOfWeek = getEndOfWeek(new Date());

  queryClient.prefetchQuery({
    queryKey: ['weekly-calendar-events', userId, startOfWeek],
    queryFn: async () => {
      const { data } = await supabase
        .from('calendar_events')
        .select('*')
        .gte('event_date', startOfWeek)
        .lte('event_date', endOfWeek)
        .order('event_date', { ascending: true });
      return data || [];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};
